import { useState, useEffect } from 'react';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { useCategories, useCurrentUser, useCreateCategory, useUpdateCategory, useDeleteCategory, useToggleCategoryActive } from '@/hooks/useQueries';

interface Category {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export default function Categories() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  const { data: categoriesData, isLoading: categoriesLoading, error: categoriesError } = useCategories({
    per_page: 100,
  });

  const createCategoryMutation = useCreateCategory();
  const updateCategoryMutation = useUpdateCategory();
  const deleteCategoryMutation = useDeleteCategory();
  const toggleCategoryActiveMutation = useToggleCategoryActive();

  const [categories, setCategories] = useState<Category[]>([]);
  const loading = categoriesLoading || userLoading;
  const [formData, setFormData] = useState({ name: '', is_active: true });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (categoriesError) {
      const errorMessage = categoriesError instanceof Error
        ? categoriesError.message
        : 'Failed to fetch categories. Please try again.';
      setError(errorMessage);
      setCategories([]);
      return;
    }

    if (categoriesData !== undefined && !categoriesLoading) {
      let apiCategories: any[] = [];

      if (Array.isArray(categoriesData)) {
        apiCategories = categoriesData;
      } else if (categoriesData?.data && Array.isArray(categoriesData.data)) {
        apiCategories = categoriesData.data;
      }

      const transformedCategories: Category[] = apiCategories.map((cat: any) => ({
        id: cat.id,
        name: cat.name || '',
        is_active: cat.is_active !== undefined ? cat.is_active : true,
        created_at: cat.created_at,
        updated_at: cat.updated_at,
      }));

      setCategories(transformedCategories);
    }
  }, [categoriesData, categoriesLoading, categoriesError]);

  const handleAdd = () => {
    setFormData({ name: '', is_active: true });
    setError('');
    setSuccess('');
    setShowAddModal(true);
  };

  const handleEdit = (category: Category) => {
    setSelectedCategory(category);
    setFormData({ name: category.name, is_active: category.is_active });
    setError('');
    setSuccess('');
    setShowEditModal(true);
  };

  const handleDelete = (category: Category) => {
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  const handleToggleActive = async (category: Category) => {
    try {
      await toggleCategoryActiveMutation.mutateAsync(category.id);
      setSuccess(`Category ${category.is_active ? 'deactivated' : 'activated'} successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update category status');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      if (showAddModal) {
        await createCategoryMutation.mutateAsync({
          name: formData.name.trim(),
          is_active: formData.is_active,
        });
        setSuccess('Category created successfully');
        setShowAddModal(false);
      } else if (showEditModal && selectedCategory) {
        await updateCategoryMutation.mutateAsync({
          id: selectedCategory.id,
          data: { name: formData.name.trim(), is_active: formData.is_active },
        });
        setSuccess('Category updated successfully');
        setShowEditModal(false);
        setSelectedCategory(null);
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save category');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;
    setDeleting(true);

    try {
      await deleteCategoryMutation.mutateAsync(categoryToDelete.id);
      setShowDeleteModal(false);
      setCategoryToDelete(null);
      setSuccess('Category deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setShowDeleteModal(false);
      setCategoryToDelete(null);
      setError(err.message || 'Failed to delete category. Please try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setDeleting(false);
    }
  };

  if (!isAdmin) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <i className="ri-error-warning-line text-6xl text-gray-300 mb-3"></i>
          <p className="text-sm text-gray-500">You do not have permission to access this page.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categories</h1>
          <p className="text-sm text-gray-600 mt-1">Manage product categories</p>
        </div>

        {/* Toast — error */}
        {error && (
          <div className="fixed top-4 right-4 z-50 min-w-[300px] max-w-md shadow-lg flex rounded-sm overflow-hidden border border-red-200">
            <div className="w-1 bg-red-500 flex-shrink-0" />
            <div className="flex items-start gap-2.5 flex-1 bg-red-50 px-3 py-2.5">
              <i className="ri-error-warning-fill text-red-500 text-sm mt-0.5 flex-shrink-0" aria-hidden />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-red-900">Error</p>
                <p className="text-xs text-red-700 mt-0.5">{error}</p>
              </div>
              <button onClick={() => setError('')} className="text-red-400 hover:text-red-600 flex-shrink-0">
                <i className="ri-close-line text-sm" />
              </button>
            </div>
          </div>
        )}

        {/* Toast — success */}
        {success && (
          <div className="fixed top-4 right-4 z-50 min-w-[300px] max-w-md shadow-lg flex rounded-sm overflow-hidden border border-emerald-200">
            <div className="w-1 bg-emerald-500 flex-shrink-0" />
            <div className="flex items-start gap-2.5 flex-1 bg-emerald-50 px-3 py-2.5">
              <i className="ri-checkbox-circle-fill text-emerald-500 text-sm mt-0.5 flex-shrink-0" aria-hidden />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-emerald-900">Success</p>
                <p className="text-xs text-emerald-700 mt-0.5">{success}</p>
              </div>
              <button onClick={() => setSuccess('')} className="text-emerald-400 hover:text-emerald-600 flex-shrink-0">
                <i className="ri-close-line text-sm" />
              </button>
            </div>
          </div>
        )}

        {/* Categories Table */}
        <div className="bg-white rounded border border-gray-200 overflow-hidden max-w-2xl">
          {loading ? (
            <div className="text-center py-12">
              <span className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#50b3e1] inline-block"></span>
              <p className="text-sm text-gray-500 mt-2">Loading categories...</p>
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-folder-line text-6xl text-gray-300 mb-3"></i>
              <p className="text-sm text-gray-500">No categories found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="pl-4 pr-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                    <th className="px-3 pr-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {categories.map((category) => (
                    <tr key={category.id} className="hover:bg-gray-50">
                      <td className="pl-4 pr-3 py-4">
                        <div className="text-sm font-medium text-gray-900">{category.name}</div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          category.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {category.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-3 pr-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleToggleActive(category)}
                            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                              category.is_active
                                ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                : 'bg-green-100 text-green-800 hover:bg-green-200'
                            }`}
                            title={category.is_active ? 'Deactivate' : 'Activate'}
                          >
                            <i className={category.is_active ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                          </button>
                          <button
                            onClick={() => handleEdit(category)}
                            className="text-[#50b3e1] hover:text-[#0997a4] transition-colors p-0.5"
                            title="Edit"
                          >
                            <i className="ri-edit-line text-base"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(category)}
                            className="text-[#ed1438] hover:text-[#c01030] transition-colors p-0.5"
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line text-base"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add Category Button */}
        <div className="max-w-2xl">
          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-[#50b3e1] text-white rounded text-sm font-medium hover:bg-[#0997a4] transition-colors cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>
            Add Category
          </button>
        </div>

        {/* Add / Edit Modal */}
        {(showAddModal || showEditModal) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {showAddModal ? 'Add Category' : 'Edit Category'}
                </h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setSelectedCategory(null);
                    setError('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name <span className="text-[#ed1438]">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter category name"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 text-[#50b3e1] border-gray-300 rounded focus:ring-[#50b3e1]"
                    />
                    <span className="text-sm text-gray-700">Active (visible in dropdowns)</span>
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setShowEditModal(false);
                      setSelectedCategory(null);
                      setError('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-[#50b3e1] text-white rounded-lg text-sm font-medium hover:bg-[#0997a4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Saving...' : showAddModal ? 'Create' : 'Update'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && categoryToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <i className="ri-error-warning-line text-4xl text-red-500"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Category</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Are you sure you want to delete <strong>{categoryToDelete.name}</strong>?
                    This action cannot be undone. Categories with associated items cannot be deleted.
                  </p>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowDeleteModal(false);
                        setCategoryToDelete(null);
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmDelete}
                      disabled={deleting}
                      className="flex-1 px-4 py-2 bg-[#ed1438] text-white rounded-lg text-sm font-medium hover:bg-[#c01030] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deleting ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
